import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { xor } from 'lodash'

import { useRemoveFromBatchForm } from '../removeFromBatchModal/useRemoveFromBatchForm/useRemoveFromBatchForm'
import { useBulkDisbursementAction } from '../useBulkDisbursementAction'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { RemoveFromBatchBulkProps } from './RemoveFromBatchBulk.types'
import { AcknowledgeModal, ActionModal, FormField, Modal, Select, Spacer, Text, TextArea } from '@percent/lemonade'

export function RemoveFromBatchBulk({
  isOpen,
  onClose,
  selectedDisbursementIds,
  data,
  refreshData,
  onSelectedDisbursementIdsChange
}: Readonly<RemoveFromBatchBulkProps>) {
  const { t } = useTranslation()
  const { disbursementsService } = useServices()
  const disbursementCancellationReasonFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.AD_DISBURSEMENT_CANCELLATION_REASON
  )

  const [selectedReason, setSelectedReason] = useState<string>('')

  const bulkCancelWithReason = ({ ids, reason }: { ids: string[]; reason?: string }) =>
    disbursementsService.bulkCancelDisbursements(ids, reason)

  const { summary, isPending, execute, resetSummary } = useBulkDisbursementAction(bulkCancelWithReason, arg => arg.ids)

  const selectedTypes = useMemo(() => {
    const types = data.filter(d => selectedDisbursementIds.includes(d.id)).map(d => d.type)

    return Array.from(new Set(types))
  }, [data, selectedDisbursementIds])

  const handleClose = () => {
    if (isPending) {
      return
    }

    refreshData?.()
    onSelectedDisbursementIdsChange(xor(selectedDisbursementIds, summary?.succeededDisbursementIds ?? []))
    resetSummary()
    onClose()
  }

  const executeBulkCancel = async (reason?: string) => {
    if (selectedDisbursementIds.length === 0) {
      return
    }

    await execute({ ids: selectedDisbursementIds, reason })
  }

  const { formik, reasonOptions } = useRemoveFromBatchForm({
    onFormSubmit: async ({ reason }) => {
      await executeBulkCancel(reason)
    },
    disbursementTypes: selectedTypes
  })

  const onReasonChange = (reason: string) => {
    setSelectedReason(reason)
    formik.setFieldValue('cancelReason', reason)
  }

  const { values, touched, errors, isValid, handleChange, handleBlur } = formik

  const failedCount = summary?.failedDisbursementIds.length ?? 0
  const succeededCount = summary?.succeededDisbursementIds.length ?? 0
  const hasSummary = !!summary

  const successModal = hasSummary && succeededCount > 0 && (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.bulkDisbursementsRemoval.result.success.title', { count: succeededCount })}
      description={
        succeededCount !== selectedDisbursementIds.length
          ? t('dialog.bulkDisbursementsRemoval.result.success.description', {
              count: failedCount,
              allCount: selectedDisbursementIds.length
            })
          : undefined
      }
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="bulk-disbursements-removal-success-modal"
      buttonTestId="bulk-disbursements-removal-success-close-button"
    />
  )

  const errorModal = hasSummary && succeededCount === 0 && failedCount > 0 && (
    <AcknowledgeModal
      result="negative"
      title={t('dialog.bulkDisbursementsRemoval.result.error.description', { count: failedCount })}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="bulk-disbursements-removal-error-modal"
      buttonTestId="bulk-disbursements-removal-error-close-button"
    />
  )

  const legacyModalContent = (
    <ActionModal
      title={t('dialog.bulkDisbursementsRemoval.title')}
      primaryButtonText={t('button.confirm')}
      secondaryButtonText={t('button.cancel')}
      type="submit"
      loading={isPending}
      disabled={isPending || selectedDisbursementIds.length === 0}
      handleClose={handleClose}
      handleSubmit={() => executeBulkCancel()}
      primaryBtnTestId="bulk-disbursements-removal-submit-button"
      secondaryBtnTestId="bulk-disbursements-removal-cancel-button"
    >
      <Text size="small">
        {t('dialog.bulkDisbursementsRemoval.description', { count: selectedDisbursementIds.length })}
      </Text>
    </ActionModal>
  )

  const reasonModalContent = (
    <ActionModal
      title={t('dialog.bulkDisbursementsRemoval.reasonTitle', { count: selectedDisbursementIds.length })}
      primaryButtonText={t('button.remove')}
      loadingText={t('button.removing')}
      secondaryButtonText={t('button.cancel')}
      type="submit"
      loading={isPending}
      disabled={!isValid || isPending}
      handleClose={handleClose}
      handleSubmit={() => formik.handleSubmit()}
      primaryBtnTestId="bulk-disbursements-removal-submit-button"
      secondaryBtnTestId="bulk-disbursements-removal-cancel-button"
    >
      <div>
        <Text size="small">
          {t('dialog.bulkDisbursementsRemoval.reasonDescription', { count: selectedDisbursementIds.length })}
        </Text>
        <Spacer axis="vertical" size={6} />
        <form>
          <FormField label={t('dialog.removeFromBatch.removalReason')} data-testid="cancelReason" necessity="required">
            <Select
              data-testid="cancelReasonSelect"
              placeholder={t('dialog.removeFromBatch.selectReason')}
              options={reasonOptions}
              defaultValue={reasonOptions.find(option => option.value === values.cancelReason) ?? undefined}
              onChange={({ value }) => onReasonChange(value)}
              maxDropdownHeight="200px"
            />
          </FormField>
          <Spacer axis="vertical" size={6} />
          {selectedReason === 'Other' && (
            <FormField
              label={t('dialog.removeFromBatch.customReason')}
              necessity="required"
              status={touched.customReason && errors.customReason ? 'danger' : 'default'}
              statusMessage={errors.customReason || ''}
              data-testid="customReason"
            >
              <TextArea
                name="customReason"
                value={values.customReason || ''}
                placeholder={t('dialog.removeFromBatch.customReasonPlaceholder')}
                maxLength={255}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </FormField>
          )}
        </form>
      </div>
    </ActionModal>
  )

  const formContent = disbursementCancellationReasonFeatureFlag ? reasonModalContent : legacyModalContent

  return (
    <Modal open={isOpen} onClose={handleClose}>
      {successModal || errorModal || formContent}
    </Modal>
  )
}
